import React, {
  forwardRef,
  useImperativeHandle,
  useRef,
  RefObject,
  useEffect,
} from 'react';
import { showError } from 'shared/toaster';
import {
  FormTemplate,
  FormTemplateSection,
} from 'data/template/template.types';
import { useTranslation } from 'react-i18next';
import * as types from 'data/xtp-xdp-connection/xtp-xdp-connection.types';
import Form, { FieldUpdateHandlerParam } from 'shared/form';
import { FormLayout } from 'components/FormLayout';
import SectionLabel from 'components/SectionLabel';
import './xtp-xdp-connection-form.scss';

type Props = {
  template: FormTemplate;
  data: Partial<types.XtpXdpConnection>;
  i18nKeyPrefix?: string;
  isEditing?: boolean;
  onFieldUpdate?: (
    param: FieldUpdateHandlerParam,
    formSection?: 'main' | 'protocol' | 'protocolInterface',
    protolType?: string,
  ) => void;
};

export type Ref = {
  isValid: (forceValidation: boolean, forceTouched: boolean) => boolean;
  getValues: () => { main: types.XtpXdpConnection, protocols: types.XtpXdpConnectionProtocol[] };
};

export const XDP_I18N_KEY =
  'xtpXdpConnection.form';

export const XtpXdpConnectionForm = forwardRef<Ref, Props>(
  (
    {
      template,
      data,
      i18nKeyPrefix = XDP_I18N_KEY,
      onFieldUpdate = () => null,
    }: Props,
    ref,
  ) => {
    const { t } = useTranslation();
    const mainRef = useRef<Form>(null);
    const protocolRef = useRef<Form>(null);
    const protocolInterfacesRefs = useRef<(RefObject<Form>)[]>([]);

    const customFieldComponentMap = {
      privateKey: SectionLabel,
      certificate: SectionLabel,
    };

    const isValid = (forceValidation = false, forceTouched = true) => {
      const isMainValid =
        mainRef.current?.isValid(forceValidation, forceTouched) ?? true;

      const isProtocolsValid = protocolInterfacesRefs.current
        .filter((entry) => entry.current != null)
        .map((entry) => entry.current?.isValid(true, true) ?? true)
        .every((valid: boolean | undefined) => valid === true);

      const protocols = protocolInterfacesRefs.current
        .filter((entry) => entry.current != null)
        .map((entry) => entry?.current?.getValues());

        if (protocols.length === 0) {
          showError('Select at least one protocol');
        }

      return isMainValid && isProtocolsValid && protocols.length > 0;
    };

    const getValues = () => {
      const formValues: { main: types.XtpXdpConnection, protocols: types.XtpXdpConnectionProtocol[] } = {
        main: mainRef.current?.getValues(),
        protocols: protocolInterfacesRefs.current
          .filter((entry) => entry.current != null)
          .map((entry) => entry?.current?.getValues())
      };
      return formValues;
    };

    useImperativeHandle(ref, () => ({
      isValid,
      getValues,
    }));

    const onMainFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'main');

    const handleProtocolFieldUpdate = (param: FieldUpdateHandlerParam) =>
      onFieldUpdate(param, 'protocol');

    const handleProtocolInterfaceFieldUpdate = (type: string) => (param: FieldUpdateHandlerParam) => {
      onFieldUpdate(param, 'protocolInterface', type);
    }

    const createNetElementsForm = (netData?:types.XtpXdpConnectionProtocol) => {
      if (netData == null) {
        return;
      }

      const netElementRef: { current: Form | null } = {
        current: null,
      };

      protocolInterfacesRefs.current.push(netElementRef);

      const refCallback = (node: Form | null) => {
        netElementRef.current = node;
      };

      return (
        <div key={netData.id as string || netData.type as string} className="xtp-xdp-connection-form-protocols">
          <div data-testid="ProtocolInterfaces">
            <p className="xtp-xdp-connection-form-protocol-type">{netData.type}</p>
            <Form
              ref={refCallback}
              name={template.formId ?? template.key}
              defaults={netData}
              fields={(template.protocolInterface as FormTemplateSection).fields}
              layout={(template.protocolInterface as FormTemplateSection).metadata.layout?.rows}
              i18nKeyPrefix={i18nKeyPrefix}
              fieldUpdateHandler={handleProtocolInterfaceFieldUpdate(netData.type)}
            />
          </div>
        </div>
      );
    }

    const renderProtolInterfaces = () => {
      const protocols = Object.keys(data.protocols || {});
      if (!protocols || data.protocols == null) {
        return null;
      }

      return protocols
        .map((type) => data?.protocols?.[type])
        .filter((item) => item?.operation !== 'DELETE')
        .map(createNetElementsForm);
    }

    return (
      <div
        className="xtp-xdp-connection-form"
        data-testid="XtpXdpConnectionForm"
      >
        <FormLayout>
          <Form
            ref={mainRef}
            name={template.formId ?? template.key}
            defaults={data}
            fields={(template.main as FormTemplateSection).fields}
            layout={
              (template.main as FormTemplateSection).metadata.layout?.rows
            }
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={onMainFieldUpdate}
            customFieldComponentMap={customFieldComponentMap}
          />
          <p className="xtp-xdp-connection-form-protocol-title">{t('xtpXdpConnection.form.protcolTitle')}</p>
          <Form
            ref={protocolRef}
            name={template.formId ?? template.key}
            defaults={data}
            fields={(template.protocol as FormTemplateSection).fields}
            layout={
              (template.protocol as FormTemplateSection).metadata.layout?.rows
            }
            i18nKeyPrefix={i18nKeyPrefix}
            fieldUpdateHandler={handleProtocolFieldUpdate}
            customFieldComponentMap={customFieldComponentMap}
          />
          {renderProtolInterfaces()}
        </FormLayout>
      </div>
    );
  },
);

export default XtpXdpConnectionForm;
